// CSS Modules - component class name prefix: CountrySwitcher__

<template>
  <div
    v-show="!loading"
    :class="[
      { CountrySwitcher__container__active: this.countrySwitcherActive },
      $style.container
    ]"
  >
    <div
      :class="$style.toggle"
      v-tippy="tippyOptions"
      ref="tippy"
      @init="initTippy"
      @hide="toggleSwitcherInactive"
      @show="toggleSwitcherActive"
    >
      <i class="fas fa-globe-americas"></i>
      <span>{{ config.currentCountry }}</span>
    </div>
    <span :class="$style.caret"></span>
    <div id="countrySwitcher" :class="$style.layer">
      <Button
        :class="$style.close"
        isClose
        colour="white"
        v-on:close="closeCountrySwitcher"
      />
      <div :class="$style.content">
        <Tabs :tabCount="2">
          <template v-slot:1-title>{{ config.websitesTitle }}</template>
          <template v-slot:1-content>
            <div :class="$style.websites">
              <div :class="[$style.websitesControls, 'columns']">
                <div class="column">
                  <Select
                    :key="`sitesKey-${componentKey}`"
                    :class="$style.regionSelect"
                    colour="blue"
                    :selectOptions="siteSelectOptions"
                    :selectValue="siteSelectValue"
                    @onChange="updateCountryButtons"
                  />
                </div>
                <div
                  class="column is-offset-one-third-tablet is-offset-half-desktop is-offset-three-fifths-widescreen"
                >
                  <Button
                    :class="$style.corporate"
                    isLink
                    colour="orange"
                    :text="config.corporateTitle"
                    :href="config.corporateUrl"
                  />
                </div>
              </div>
              <div :class="$style.countries">
                <div
                  v-for="(country, index) in countryButtons"
                  :key="`CountryButton${country.name}${index}`"
                >
                  <CountryButton
                    :country="country.name"
                    :flag="country.flag"
                    :languages="country.languages"
                  />
                </div>
              </div>
            </div>
          </template>
          <template v-slot:2-title>{{ config.locationsTitle }}</template>
          <template v-slot:2-content>
            <div :class="$style.locations">
              <div :class="[$style.locationsControls, 'columns is-multiline']">
                <div class="column is-one-third-tablet is-one-quarter-desktop">
                  <Select
                    :class="$style.regionSelect"
                    colour="blue"
                    :selectOptions="regionSelectOptions"
                    :selectValue="regionSelectValue"
                    @onChange="updateCountries"
                  />
                </div>
                <div class="column is-one-third-tablet is-one-quarter-desktop">
                  <Select
                    :key="`countryKey-${componentKey}`"
                    :class="$style.countrySelect"
                    colour="white"
                    :selectOptions="countrySelectOptions"
                    :selectValue="countrySelectValue"
                    :placeholder="countrySelectPlaceholder"
                    @onChange="updateContacts"
                  />
                </div>
                <div
                  class="column is-one-third-tablet is-one-quarter-desktop is-offset-one-quarter-desktop"
                >
                  <Button
                    :class="$style.headQuarters"
                    isLink
                    colour="orange"
                    :text="config.headquaterTitle"
                    :href="config.corporateUrl"
                  />
                </div>
              </div>
              <div
                :class="[
                  $style.contacts,
                  {
                    [$style.contactsWithSpace]:
                      contacts && contacts.length > contactsToShow
                  }
                ]"
              >
                <div v-if="contacts">
                  <agile
                    ref="slider"
                    :dots="false"
                    :slidesToShow="1"
                    :infinite="false"
                    @breakpoint="resetSlider"
                    :navButtons="contacts && contacts.length > 1"
                    :responsive="[
                      {
                        breakpoint: 480,
                        settings: {
                          slidesToShow: 2,
                          navButtons: contacts && contacts.length > 2
                        }
                      },
                      {
                        breakpoint: 768,
                        settings: {
                          slidesToShow: 3,
                          navButtons: contacts && contacts.length > 3
                        }
                      },
                      {
                        breakpoint: 1024,
                        settings: {
                          slidesToShow: 4,
                          navButtons: contacts && contacts.length > 4
                        }
                      }
                    ]"
                  >
                    <div v-for="(contact, index) in contacts" :key="index">
                      <h6 v-if="contact.company">{{ contact.company }}</h6>
                      <address
                        v-if="contact.address"
                        v-html="
                          `${contact.address.replace(
                            /(?:\r\n|\r|\n)/g,
                            '<br>'
                          )}<br>${contact.country}`
                        "
                      ></address>
                      <dl v-if="contact.phone || contact.fax">
                        <dt v-if="contact.phone">Tel.:</dt>
                        <dd v-if="contact.phone">{{ contact.phone }}</dd>
                        <br />
                        <dt v-if="contact.fax">Fax:</dt>
                        <dd v-if="contact.fax">{{ contact.fax }}</dd>
                      </dl>
                      <a
                        v-if="contact.email"
                        :href="`mailto:${contact.email}`"
                        >{{ contact.email }}</a
                      >
                      <br />
                      <a
                        v-if="contact.website"
                        target="_blank"
                        :href="contact.website"
                        >{{ contact.website }}</a
                      >
                    </div>
                  </agile>
                </div>
              </div>
            </div>
          </template>
        </Tabs>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
//import { isMobile } from "mobile-device-detect";
import { VueAgile } from "../vue-agile";
import Button from "../Button/Button.vue";
import CountryButton from "../CountryButton/CountryButton.vue";
import Select from "../Select/Select.vue";
import Tabs from "../Tabs/Tabs.vue";
import { regionDataUrl } from "../../shared/globals";

export default {
  name: "CountrySwitcher",
  components: {
    Button,
    CountryButton,
    Select,
    Tabs,
    agile: VueAgile
  },
  props: {
    config: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      loading: true,
      deactivateSlider: false,
      componentKey: 0,
      apiData: null,
      contacts: [],
      siteSelectOptions: [],
      siteSelectValue: "",
      countryButtons: [],
      regionSelectOptions: [],
      regionSelectValue: "",
      countrySelectOptions: [],
      countrySelectValue: null,
      countrySelectPlaceholder: "",
      countrySwitcherActive: false,
      contactsToShow: 1,
      tippyOptions: {
        theme: "wika light",
        interactive: true,
        reactive: true,
        placement: "bottom",
        flip: false,
        animation: "perspective",
        animateFill: false,
        arrow: true,
        distance: 7,
        zIndex: 10000,
        html: "#countrySwitcher",
        trigger: "click" //isMobile ? "click" : "mouseenter focus"
      }
    };
  },
  methods: {
    calcContactsToShow() {
      this.contactsToShow =
        window.innerWidth >= 1024
          ? 4
          : window.innerWidth >= 768
          ? 3
          : window.innerWidth >= 480
          ? 2
          : 1;
    },
    resetSlider() {
      this.$refs.slider.goTo(0);
    },
    forceRerender() {
      this.componentKey += 1;
    },
    initTippy(tippy) {
      tippy.popper.id = "countrySwitcherPopper";
    },
    toggleSwitcherActive() {
      this.countrySwitcherActive = true;
    },
    toggleSwitcherInactive() {
      this.countrySwitcherActive = false;
    },
    closeCountrySwitcher() {
      this.$refs.tippy._tippy.hide();
    },
    getContacts(url) {
      this.contacts = null;
      axios
        .get(url)
        .then(response => {
          this.contacts = response.data.contacts;
        })
        .catch(error => {
          console.error(error);
        });
    },
    updateContacts(value) {
      this.countrySelectValue = value;

      const region = this.apiData.regions.find(
        region => region.name === this.regionSelectValue
      );

      if (region) {
        const country = region.countries.find(
          country => country.name === value
        );

        if (country) {
          this.getContacts(country.contacts);
        }
      }
    },
    initApiData(data) {
      this.apiData = data;
      this.siteSelectValue = this.config.currentRegion;
      this.regionSelectValue = this.config.currentRegion;
      this.countrySelectValue = this.config.currentCountry;
      this.countrySelectPlaceholder = this.config.countrySelectPlaceholder;

      for (let region of this.apiData.regions) {
        this.siteSelectOptions.push(region.name);
        this.regionSelectOptions.push(region.name);

        if (region.name === this.config.currentRegion) {
          for (let country of region.countries) {
            this.countrySelectOptions.push(country.name);

            if (
              country.flag &&
              country.languages &&
              country.languages.length !== 0
            ) {
              this.countryButtons.push(country);
            }

            if (country.name === this.config.currentCountry) {
              this.getContacts(country.contacts);
            }
          }
        }
      }
      this.loading = false;
      this.forceRerender();
    },
    updateCountryButtons(value) {
      this.siteSelectValue = value;

      const region = this.apiData.regions.find(region => region.name === value);

      if (region) {
        this.countryButtons.length = 0;

        for (let country of region.countries) {
          if (
            country.flag &&
            country.languages &&
            country.languages.length !== 0
          ) {
            this.countryButtons.push(country);
          }
        }
      }
    },
    updateCountries(value) {
      this.regionSelectValue = value;
      this.countrySelectValue = this.countrySelectPlaceholder;
      this.countrySelectOptions.length = 0;
      this.contacts = [];

      const region = this.apiData.regions.find(region => region.name === value);

      if (region) {
        for (let country of region.countries) {
          this.countrySelectOptions.push(country.name);
        }
      }

      this.forceRerender();
    }
  },
  mounted() {
    window.addEventListener("resize", this.calcContactsToShow);
    this.calcContactsToShow();
    let dataUrl = "";
    window.regionDataUrl
      ? (dataUrl = window.regionDataUrl)
      : (dataUrl = regionDataUrl);
    axios
      .get(dataUrl)
      .then(response => this.initApiData(response.data))
      .catch(error => {
        this.loading = false;
        console.error(error);
      });
  },
  destroyed() {
    window.removeEventListener("resize", this.calcContactsToShow);
  }
};
</script>

<style module lang="scss">
@import "./CountrySwitcher.scss";
</style>
